import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

function parseKVString(s: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!s) return out;
  const trimmed = s.replace(/^([#?])/, '');
  for (const part of trimmed.split('&')) {
    if (!part) continue;
    const eq = part.indexOf('=');
    const k = eq >= 0 ? part.slice(0, eq) : part;
    const v = eq >= 0 ? part.slice(eq + 1) : '';
    try {
      const key = decodeURIComponent(k);
      const val = decodeURIComponent(v.replace(/\+/g, '%20'));
      out[key] = val;
    } catch {
      // swallow decode errors and keep raw
      out[k] = v;
    }
  }
  return out;
}

const AuthCallback: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Prefer fragment for implicit flow; fall back to query string if needed
    const hashParams = parseKVString(location.hash || '');
    const queryParams = parseKVString(location.search || '');
    const params = Object.keys(hashParams).length ? hashParams : queryParams;

    const accessToken = params['access_token'];
    const idToken = params['id_token'];
    const tokenType = params['token_type'];
    const expiresIn = params['expires_in'];
    const state = params['state'];
    const error = params['error'];
    const errorDescription = params['error_description'];

    if (accessToken) localStorage.setItem('access_token', accessToken);
    if (idToken) localStorage.setItem('id_token', idToken);
    if (tokenType) localStorage.setItem('token_type', tokenType);
    if (expiresIn) localStorage.setItem('expires_in', expiresIn);

    // If Cognito returned an error, send the user back to /login with details.
    if (error) {
      const search = new URLSearchParams({ error, error_description: errorDescription || '' }).toString();
      window.location.replace(`/login?${search}`);
      return;
    }

    // Redirect to original path or dashboard. `state` is already decoded.
    let to = state && typeof state === 'string' && state.length > 0 ? state : undefined;
    if (!to) {
      try {
        const stored = sessionStorage.getItem('returnTo');
        if (stored) to = stored;
        sessionStorage.removeItem('returnTo');
      } catch {}
    }

    // Sanitize and normalize the redirect target
    if (to) {
      // Normalize escaped leading slash variants like "\/" => "/"
      to = to.replace(/^\\+\//, '/');
      // Ensure relative, same-origin path only
      if (/^https?:\/\//i.test(to)) {
        try {
          const u = new URL(to);
          if (u.origin !== window.location.origin) to = '/dashboard';
          else to = u.pathname + u.search + u.hash;
        } catch {
          to = '/dashboard';
        }
      }
      // Must start with a slash
      if (!to.startsWith('/')) to = '/dashboard';
    }

    if (!to) to = '/dashboard';
    // Full replace to remount App/AuthProvider cleanly (ensures token is read on load)
    window.location.replace(to);
  }, [location.hash, location.search]);

  return null;
};

export default AuthCallback;
